import type { Language } from "../api";

/**
 * Finding a language by typing (ADR-0056). Every ISO 639-1 language is on the
 * served list, so the picker searches rather than lists.
 *
 * A plain "contains" over folded text, never `\b`: word boundaries are ASCII in
 * JavaScript and fire mid-word in Arabic script (CLAUDE.md).
 */

/**
 * fold makes two spellings of one name compare equal: lower case, no combining
 * marks ("Español" is "espanol"), Arabic yeh and kaf as Persian, and no ZWNJ.
 */
export function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[يى]/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/‌/g, "")
    .trim();
}

/**
 * matchLanguages is what the picker shows for a query: the common languages
 * when there is none, otherwise every language whose code, English name,
 * endonym or Persian name contains it — those that start with it first, then
 * by English name.
 */
export function matchLanguages(languages: Language[], query: string): Language[] {
  const q = fold(query);
  if (q === "") return languages.filter((l) => l.common);
  const names = (l: Language) => [l.code, l.english, l.endonym, l.persian ?? ""].map(fold);
  const hits = languages.filter((l) => names(l).some((name) => name.includes(q)));
  const starts = (l: Language) => (names(l).some((name) => name.startsWith(q)) ? 0 : 1);
  return hits.sort((a, b) => starts(a) - starts(b) || a.english.localeCompare(b.english));
}
